/**
 * O orçamento e planejamento (módulo 19; 0106): o que a mesa e o celular
 * mostram e escrevem, sem rede, para o teste alcançar.
 *
 * Nenhuma conta mora aqui: o realizado, o desvio, o cenário e a projeção de
 * caixa vêm do servidor, com as premissas e as frases ("cenário não é
 * previsão", o que só o ERP terá). Aqui está o que é da tela — o endereço, o
 * nome de cada coisa, o formulário da linha e o retorno das gravações (R04).
 */
import { formatarCompetencia, formatarDecimal, formatarDia, formatarReais, interpretarNumero } from '@/lib/formato';
import type { RetornoDeclarado } from '@/lib/retorno';

import type { CodigoDaPremissa, Direcionador, LinhaDoDesvio, LinhaParaGravar, Natureza, OrigemDaLinha, OrigemDoEvento, Situacao, VariaveisDoCenario } from './api';

/* ----------------------------------------------------- o endereço da tela */

export const ABAS_DO_ORCAMENTO = ['orcamento', 'desvios', 'cenarios', 'caixa'] as const;
export type AbaDoOrcamento = (typeof ABAS_DO_ORCAMENTO)[number];

export interface BuscaDoOrcamento {
  /** Ausente = `orcamento`. */
  readonly aba?: Exclude<AbaDoOrcamento, 'orcamento'>;
  readonly versao?: string;
  /** A competência do previsto × realizado, `aaaa-mm`. */
  readonly competencia?: string;
  /** 0109: o desvio que abre no painel (`centro|conta`), pelo aviso do sino. */
  readonly desvio?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validarBuscaDoOrcamento(entrada: Record<string, unknown>): BuscaDoOrcamento {
  const aba = entrada['aba'];
  const versao = entrada['versao'];
  const competencia = entrada['competencia'];
  const desvio = entrada['desvio'];
  return {
    ...(aba === 'desvios' || aba === 'cenarios' || aba === 'caixa' ? { aba } : {}),
    ...(typeof versao === 'string' && UUID.test(versao) ? { versao } : {}),
    ...(typeof competencia === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(competencia) ? { competencia } : {}),
    ...(typeof desvio === 'string' && desvio.split('|').length === 2 && desvio.split('|').every((x) => UUID.test(x)) ? { desvio } : {}),
  };
}

/** `aaaa-mm-01` do mês de `quando`. */
export function competenciaDe(quando: Date): string {
  return `${String(quando.getFullYear())}-${String(quando.getMonth() + 1).padStart(2, '0')}-01`;
}

/** `aaaa-mm-01` do mês `n` meses depois (ou antes). */
export function somarMeses(competencia: string, n: number): string {
  const [ano, mes] = competencia.split('-').map(Number) as [number, number];
  const total = ano * 12 + (mes - 1) + n;
  return `${String(Math.floor(total / 12))}-${String((total % 12) + 1).padStart(2, '0')}-01`;
}

/** A chave da consulta do previsto × realizado: a lista e o painel leem a mesma. */
export const chaveDaConsultaDosDesvios = (competencia: string, versaoId: string | null) => ['orcamento', 'desvios', competencia, versaoId] as const;

/** O último mês fechado: o padrão do previsto × realizado (o mês corrente ainda está em andamento). */
export const ultimoMesFechado = (hoje: Date = new Date()): string => somarMeses(competenciaDe(hoje), -1);

const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'] as const;
/** "set" de "2026-09-01": o cabeçalho da coluna da grade. */
export const mesCurto = (competencia: string): string => MESES_CURTOS[Number(competencia.slice(5, 7)) - 1] ?? competencia;

/* -------------------------------------------------- os nomes de cada coisa */

export const NOME_DA_SITUACAO: Readonly<Record<Situacao, string>> = {
  rascunho: 'Rascunho',
  aprovado: 'Aprovado',
  arquivado: 'Arquivado',
};

/**
 * O tom do selo da versão. Não são os status da máquina (p.12): é o tom de
 * retorno — aprovado verde, rascunho âmbar (falta aprovar), arquivado neutro.
 */
export const TOM_DA_SITUACAO: Readonly<Record<Situacao, 'sucesso' | 'atencao' | 'neutro'>> = {
  rascunho: 'atencao',
  aprovado: 'sucesso',
  arquivado: 'neutro',
};

export const NOME_DA_NATUREZA: Readonly<Record<Natureza, string>> = {
  receita: 'Receita',
  custo: 'Custo',
  despesa: 'Despesa',
  investimento: 'Investimento',
};

export const NOME_DO_DIRECIONADOR: Readonly<Record<Direcionador, string>> = {
  horas: 'Por horas',
  litros: 'Por litros',
  fixo: 'Valor fixo',
};

export const UNIDADE_DO_DIRECIONADOR: Readonly<Record<Exclude<Direcionador, 'fixo'>, { readonly quantidade: string; readonly preco: string }>> = {
  horas: { quantidade: 'h', preco: 'R$/h' },
  litros: { quantidade: 'L', preco: 'R$/L' },
};

export const NOME_DA_ORIGEM_DA_LINHA: Readonly<Record<OrigemDaLinha, string>> = {
  manual: 'Manual',
  historico: 'Sugestão do histórico',
  cenario: 'Cenário',
  proposta_de_investimento: 'Proposta de investimento',
};

export const NOME_DA_ORIGEM_DO_EVENTO: Readonly<Record<OrigemDoEvento, string>> = {
  documento: 'Documento a receber',
  medicao: 'Medição sem documento',
  receita_orcada: 'Receita orçada',
  fatura_de_posto: 'Fatura de posto',
  titulo_a_receber: 'Título a receber',
  pedido: 'Pedido de compra',
  titulo: 'Título de compra',
  custo_orcado: 'Custo orçado',
  investimento: 'Investimento orçado',
};

/** "R$ 1.234,56", e o negativo com o sinal de menos tipográfico. */
export function reaisComSinal(valor: number): string {
  return valor < 0 ? `−${formatarReais(Math.abs(valor))}` : formatarReais(valor);
}

/** "12,3%", com sinal; o travessão sem base — nunca "0%" de quem só não tem previsto. */
export function percentualEscrito(parte: number | null | undefined, comSinal = false): string {
  if (parte === null || parte === undefined || !Number.isFinite(parte)) return '—';
  const p = parte * 100;
  const sinal = p < 0 ? '−' : comSinal && p > 0 ? '+' : '';
  return `${sinal}${formatarDecimal(Math.abs(p), 1)}%`;
}

/** "acima do previsto" ou "abaixo", com a parte: o que o desvio diz em palavras. */
export function fraseDoDesvio(l: Pick<LinhaDoDesvio, 'desvio' | 'desvioPct' | 'previsto'>): string {
  if (l.desvio === 0) return 'no previsto';
  const lado = l.desvio > 0 ? 'acima do previsto' : 'abaixo do previsto';
  return l.previsto === 0 ? `${reaisComSinal(l.desvio)} sem previsto` : `${percentualEscrito(Math.abs(l.desvioPct ?? 0))} ${lado}`;
}

/** O valor da premissa com a unidade: "R$ 6,20/L", "30 dias", "75,0%", "14,0% a.a.". */
export function premissaEscrita(codigo: CodigoDaPremissa, valor: number | null): string {
  if (valor === null) return 'Sem premissa';
  if (codigo === 'preco_diesel') return `${formatarReais(valor)}/L`;
  if (codigo === 'prazo_medio_recebimento' || codigo === 'prazo_medio_pagamento') return `${formatarDecimal(valor, 1).replace(/,0$/, '')} dias`;
  if (codigo === 'taxa_custo_de_capital') return `${formatarDecimal(valor, 1)}% a.a.`;
  return `${formatarDecimal(valor, 1)}%`;
}

/* ------------------------------------------------------ a linha do orçamento */

export interface MesDaLinha {
  readonly quantidade: string;
  readonly preco: string;
  readonly valor: string;
}

export interface ValoresDaLinha {
  readonly id: string | null;
  readonly centroCustoId: string;
  /** A conta do plano gerencial, ou `investimento` (sem conta). */
  readonly contaId: string;
  readonly contratoId: string;
  readonly equipamentoId: string;
  readonly direcionador: Direcionador;
  readonly memoria: string;
  readonly meses: Readonly<Record<string, MesDaLinha>>;
}

export const MES_VAZIO: MesDaLinha = { quantidade: '', preco: '', valor: '' };
export const INVESTIMENTO = 'investimento';

/** O valor de um mês, em reais: o digitado, ou a quantidade vezes o preço. Nulo sem nada. */
export function valorDoMes(direcionador: Direcionador, m: MesDaLinha): number | null {
  if (direcionador === 'fixo') return m.valor.trim() === '' ? null : interpretarNumero(m.valor);
  const q = interpretarNumero(m.quantidade);
  const p = interpretarNumero(m.preco);
  return q === null || p === null ? null : Math.round(q * p * 100) / 100;
}

/** O que falta na linha, campo a campo. */
export function faltaNaLinha(v: ValoresDaLinha, competencias: readonly string[]): Partial<Record<'centro' | 'conta' | 'memoria' | 'meses', string>> {
  const falta: Partial<Record<'centro' | 'conta' | 'memoria' | 'meses', string>> = {};
  if (v.centroCustoId === '') falta.centro = 'Escolha o centro de custo.';
  if (v.contaId === '') falta.conta = 'Escolha a conta do plano gerencial, ou Investimento.';
  if (v.memoria.trim().length < 3) falta.memoria = 'Escreva a memória de cálculo, por exemplo "aluguel do pátio, contrato de 2026".';
  if (v.contaId === INVESTIMENTO && v.direcionador !== 'fixo') falta.conta = 'O investimento é de valor fixo: troque o direcionador.';
  const preenchidos = competencias.filter((c) => {
    const m = v.meses[c] ?? MES_VAZIO;
    return m.valor.trim() !== '' || m.quantidade.trim() !== '' || m.preco.trim() !== '';
  });
  if (preenchidos.length === 0) falta.meses = 'Informe o valor de ao menos um mês.';
  for (const c of preenchidos) {
    const valor = valorDoMes(v.direcionador, v.meses[c] ?? MES_VAZIO);
    if (valor === null || valor < 0) {
      falta.meses = v.direcionador === 'fixo'
        ? `Em ${formatarCompetencia(c)}, escreva o valor em reais, por exemplo 1.250,00.`
        : `Em ${formatarCompetencia(c)}, informe a quantidade e o preço.`;
      break;
    }
  }
  return falta;
}

/** O corpo da gravação. A natureza sai do grupo da conta (a mesma regra do banco). */
export function corpoDaLinha(v: ValoresDaLinha, competencias: readonly string[], naturezaDaConta: (contaId: string) => Natureza | null): LinhaParaGravar {
  const investimento = v.contaId === INVESTIMENTO;
  return {
    id: v.id,
    centroCustoId: v.centroCustoId,
    contaId: investimento ? null : v.contaId,
    natureza: investimento ? 'investimento' : (naturezaDaConta(v.contaId) ?? 'custo'),
    contratoId: v.contratoId || null,
    equipamentoId: v.equipamentoId || null,
    direcionador: v.direcionador,
    memoria: v.memoria,
    valores: competencias.flatMap((c) => {
      const m = v.meses[c] ?? MES_VAZIO;
      const valor = valorDoMes(v.direcionador, m);
      if (valor === null) return [];
      return [{
        competencia: c.slice(0, 7),
        quantidade: v.direcionador === 'fixo' ? null : interpretarNumero(m.quantidade),
        preco: v.direcionador === 'fixo' ? null : interpretarNumero(m.preco),
        valor,
      }];
    }),
  };
}

/** Repete o mês escolhido nos doze: o atalho do "mesmo valor todo mês". */
export function repetirNoAno(v: ValoresDaLinha, competencias: readonly string[], origem: string): ValoresDaLinha {
  const m = v.meses[origem] ?? MES_VAZIO;
  return { ...v, meses: Object.fromEntries(competencias.map((c) => [c, m])) };
}

/** Os valores da linha gravada, para editar. */
export function valoresDaLinhaGravada(
  l: { readonly id: string; readonly centroId: string; readonly contaId: string | null; readonly contratoId: string | null; readonly equipamentoId: string | null; readonly direcionador: Direcionador; readonly memoria: string; readonly valores: readonly { readonly competencia: string; readonly quantidade: number | null; readonly preco: number | null; readonly valor: number }[] },
): ValoresDaLinha {
  const numero = (n: number | null) => (n === null ? '' : String(n).replace('.', ','));
  /* O valor em reais vai formatado (38.000,00): `interpretarNumero` le de volta. */
  const reais = (n: number) => formatarDecimal(n, 2);
  return {
    id: l.id,
    centroCustoId: l.centroId,
    contaId: l.contaId ?? INVESTIMENTO,
    contratoId: l.contratoId ?? '',
    equipamentoId: l.equipamentoId ?? '',
    direcionador: l.direcionador,
    memoria: l.memoria,
    meses: Object.fromEntries(l.valores.map((v) => [v.competencia, { quantidade: numero(v.quantidade), preco: numero(v.preco), valor: reais(v.valor) }])),
  };
}

/* ------------------------------------------------------ o cenário */

export interface ValoresDoCenario {
  readonly nome: string;
  readonly utilizacao: string;
  readonly precoDiesel: string;
  readonly valorHora: string;
  readonly manutencao: string;
  readonly observacao: string;
}

export const CENARIO_NOVO: ValoresDoCenario = { nome: '', utilizacao: '100', precoDiesel: '', valorHora: '100', manutencao: '100', observacao: '' };

export function faltaNoCenario(v: ValoresDoCenario): Partial<Record<keyof ValoresDoCenario, string>> {
  const falta: Partial<Record<keyof ValoresDoCenario, string>> = {};
  if (v.nome.trim().length < 3) falta.nome = 'Dê um nome ao cenário, por exemplo "Pessimista".';
  const pct = (t: string, max: number) => {
    const n = interpretarNumero(t);
    return n !== null && n >= 0 && n <= max;
  };
  if (!pct(v.utilizacao, 200)) falta.utilizacao = 'A utilização vai de 0% a 200% do histórico.';
  if (!pct(v.valorHora, 200)) falta.valorHora = 'O valor-hora vai de 0% a 200% do histórico.';
  if (!pct(v.manutencao, 300)) falta.manutencao = 'A manutenção vai de 0% a 300% do histórico.';
  if (v.precoDiesel.trim() !== '') {
    const p = interpretarNumero(v.precoDiesel);
    if (p === null || p <= 0) falta.precoDiesel = 'Escreva o preço do diesel em R$/L, por exemplo 6,29, ou deixe em branco para usar o da premissa.';
  }
  return falta;
}

export function corpoDoCenario(v: ValoresDoCenario): VariaveisDoCenario {
  return {
    nome: v.nome,
    utilizacaoPct: interpretarNumero(v.utilizacao) ?? 100,
    precoDiesel: v.precoDiesel.trim() === '' ? null : interpretarNumero(v.precoDiesel),
    valorHoraPct: interpretarNumero(v.valorHora) ?? 100,
    manutencaoPct: interpretarNumero(v.manutencao) ?? 100,
    observacao: v.observacao,
  };
}

export function valoresDoCenario(c: VariaveisDoCenario): ValoresDoCenario {
  const n = (x: number) => String(x).replace('.', ',');
  return {
    nome: c.nome,
    utilizacao: n(c.utilizacaoPct),
    precoDiesel: c.precoDiesel === null ? '' : n(c.precoDiesel),
    valorHora: n(c.valorHoraPct),
    manutencao: n(c.manutencaoPct),
    observacao: c.observacao ?? '',
  };
}

/** "Utilização 80% · Diesel R$ 7,50/L · Valor-hora 95% · Manutenção 110%". */
export function variaveisEscritas(c: VariaveisDoCenario, precoDaPremissa: number | null): string {
  const diesel = c.precoDiesel ?? precoDaPremissa;
  return [
    `Utilização ${formatarDecimal(c.utilizacaoPct, 1).replace(/,0$/, '')}%`,
    `Diesel ${diesel === null ? 'sem preço' : `${formatarReais(diesel)}/L`}${c.precoDiesel === null ? ' (premissa)' : ''}`,
    `Valor-hora ${formatarDecimal(c.valorHoraPct, 1).replace(/,0$/, '')}%`,
    `Manutenção ${formatarDecimal(c.manutencaoPct, 1).replace(/,0$/, '')}%`,
  ].join(' · ');
}

/* ------------------------------------------------------ os retornos (R04) */
/* O TÍTULO é escrito em cada gravação (o teste `retorno-das-gravacoes` cobra o
   `titulo:` dentro do `retornoDe`); aqui mora o resto, igual na mesa e no
   celular. */

type DetalheDoRetorno = Omit<RetornoDeclarado, 'titulo'>;

const versaoEscrita = (r: { readonly exercicio: number; readonly versao: number }) => `${String(r.exercicio)} · versão ${String(r.versao)}`;

export function detalheDaVersaoCriada(r: { readonly exercicio: number; readonly versao: number; readonly nome: string }): DetalheDoRetorno {
  return { alvo: { codigo: versaoEscrita(r), nome: r.nome }, fatos: ['Rascunho'], efeito: 'Monte as linhas e as premissas; a aprovação é de outra pessoa' };
}

export function detalheDaRevisao(r: { readonly exercicio: number; readonly versao: number; readonly nome: string; readonly substitui: number; readonly linhas: number }): DetalheDoRetorno {
  return {
    alvo: { codigo: versaoEscrita(r), nome: r.nome },
    fatos: [`${String(r.linhas)} ${r.linhas === 1 ? 'linha copiada' : 'linhas copiadas'}`, `substitui a versão ${String(r.substitui)}`],
    efeito: 'A versão aprovada continua valendo até a revisão ser aprovada',
  };
}

export function detalheDaAprovacao(r: { readonly exercicio: number; readonly versao: number; readonly nome: string; readonly arquivada: number | null; readonly receita: number; readonly resultado: number }): DetalheDoRetorno {
  return {
    alvo: { codigo: versaoEscrita(r), nome: r.nome },
    fatos: [`receita ${formatarReais(r.receita)}`, `resultado ${reaisComSinal(r.resultado)}`],
    efeito: r.arquivada === null
      ? 'A versão não muda mais: corrigir é abrir uma revisão'
      : { rotulo: 'Versão aprovada', antes: `versão ${String(r.arquivada)}`, depois: `versão ${String(r.versao)}` },
  };
}

export function detalheDoDescarte(r: { readonly exercicio: number; readonly versao: number; readonly nome: string }, motivo: string): DetalheDoRetorno {
  return { alvo: { codigo: versaoEscrita(r), nome: r.nome }, fatos: [`motivo: ${motivo.trim()}`], efeito: { rotulo: 'Situação', antes: 'Rascunho', depois: 'Arquivado' } };
}

export function detalheDasLinhasGravadas(r: { readonly exercicio: number; readonly versao: number; readonly gravadas: number; readonly linhas: readonly { readonly memoria: string }[]; readonly total: number }): DetalheDoRetorno {
  return {
    alvo: { codigo: versaoEscrita(r), ...(r.gravadas === 1 && r.linhas[0] ? { nome: r.linhas[0].memoria } : {}) },
    fatos: [`${String(r.gravadas)} ${r.gravadas === 1 ? 'linha' : 'linhas'}`, `${formatarReais(r.total)} no exercício`],
  };
}

export function detalheDaLinhaRemovida(r: { readonly memoria: string; readonly centro: string; readonly conta: string | null; readonly total: number }): DetalheDoRetorno {
  return { alvo: { codigo: r.centro, nome: r.memoria }, fatos: [r.conta ? `conta ${r.conta}` : 'investimento', `${formatarReais(r.total)} no exercício`] };
}

export function detalheDaSugestaoAplicada(r: { readonly criadas: number; readonly mesesNoHistorico: number; readonly totalNoExercicio: number; readonly exercicio: number }): DetalheDoRetorno {
  return {
    alvo: { codigo: String(r.exercicio) },
    fatos: [`${String(r.criadas)} ${r.criadas === 1 ? 'linha' : 'linhas'}`, `média de ${String(r.mesesNoHistorico)} ${r.mesesNoHistorico === 1 ? 'mês' : 'meses'}`, `${formatarReais(r.totalNoExercicio)} no exercício`],
    efeito: 'Confira cada linha: a sugestão é o passado, e o orçamento é o plano',
  };
}

export function detalheDaPremissa(r: { readonly codigo: CodigoDaPremissa; readonly valor: number; readonly fonte: string; readonly antes: number | null }, rotulo: string): DetalheDoRetorno {
  return {
    alvo: { nome: rotulo },
    fatos: [`fonte: ${r.fonte}`],
    efeito: { rotulo, antes: premissaEscrita(r.codigo, r.antes), depois: premissaEscrita(r.codigo, r.valor) },
  };
}

export function detalheDaExplicacao(r: { readonly competencia: string; readonly centro: string | null; readonly conta: string | null; readonly nomeDaConta: string | null; readonly texto: string }): DetalheDoRetorno {
  return {
    alvo: { codigo: `${r.centro ?? ''} · ${r.conta ?? ''}`, ...(r.nomeDaConta ? { nome: r.nomeDaConta } : {}) },
    fatos: [formatarCompetencia(r.competencia), r.texto],
    efeito: 'O desvio deixa de pedir explicação; a anterior continua na história',
  };
}

export function detalheDosLimiares(r: { readonly pct: number; readonly valor: number; readonly antes: { readonly pct: number; readonly valor: number } }): DetalheDoRetorno {
  const escrito = (x: { readonly pct: number; readonly valor: number }) => `${formatarDecimal(x.pct, 1)}% e ${formatarReais(x.valor)}`;
  return { alvo: { nome: 'Desvio relevante' }, efeito: { rotulo: 'Limiares', antes: escrito(r.antes), depois: escrito(r) } };
}

export function detalheDoCenario(r: VariaveisDoCenario, precoDaPremissa: number | null): DetalheDoRetorno {
  return { alvo: { nome: r.nome }, fatos: [variaveisEscritas(r, precoDaPremissa)], efeito: 'Cenário não é previsão: compare com o orçado e com os outros cenários' };
}

export function detalheDaHipotese(r: { readonly cenario: string; readonly tipo: string; readonly contrato: string | null; readonly horasMes: number | null; readonly antes: number | null }): DetalheDoRetorno {
  const horas = (h: number | null) => (h === null ? 'média do histórico' : `${formatarDecimal(h, 1).replace(/,0$/, '')} h/mês`);
  return {
    alvo: { codigo: r.contrato ?? 'sem contrato', nome: r.tipo },
    fatos: [`cenário ${r.cenario}`],
    efeito: { rotulo: 'Demanda', antes: horas(r.antes), depois: horas(r.horasMes) },
  };
}

export function detalheDoSaldo(r: { readonly data: string; readonly valor: number; readonly fonte: string; readonly antes: { readonly data: string; readonly valor: number } | null }): DetalheDoRetorno {
  return {
    alvo: { nome: `Saldo de ${formatarDia(r.data)}` },
    fatos: [`fonte: ${r.fonte}`],
    efeito: { rotulo: 'Saldo de partida do caixa', antes: r.antes ? `${reaisComSinal(r.antes.valor)} (${formatarDia(r.antes.data)})` : 'nenhum', depois: reaisComSinal(r.valor) },
  };
}
